import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { History, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { IconAmDuong, IconDongXu, IconTrangChu } from "@/components/nav-icons";
import { HomeAccountSheet } from "@/components/home-account-sheet";

const tabs = [
  { label: "Trang chủ", to: "/", icon: IconTrangChu, match: (path: string) => path === "/" },
  {
    label: "Bói Đê",
    to: "/tong-hop",
    icon: IconAmDuong,
    match: (path: string) => path.startsWith("/chu-de") || path.startsWith("/tong-hop"),
  },
  {
    label: "Lịch sử",
    to: "/lich-su",
    icon: History,
    match: (path: string) => path.startsWith("/lich-su"),
  },
] as const;

// Các trang thuộc khu vực tài khoản — dùng để bật trạng thái active cho tab "Tôi".
const accountPaths = [
  "/thanh-toan",
  "/goi",
  "/bien-dong-so-du",
  "/gioi-thieu",
  "/hop-thu",
  "/cam-nhan",
  "/diem-danh",
];

export function BottomNav() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [accountOpen, setAccountOpen] = useState(false);
  const castingActive = pathname.startsWith("/kinh-dich");

  if (pathname === "/login") return null;

  return (
    <nav
      aria-label="Điều hướng chính"
      className="sticky bottom-0 z-40 isolate w-full shrink-0 border-t border-border/60 bg-background/92 backdrop-blur-md shadow-[0_-8px_30px_rgba(25,15,5,0.08)] md:hidden"
      style={{ paddingBottom: "var(--bottom-nav-pad)" }}
    >
      <div className="relative mx-auto grid h-[var(--bottom-nav-row)] max-w-md grid-cols-5 items-end px-1">
        {tabs.slice(0, 2).map((tab) => {
          const active = tab.match(pathname);
          return <NavTab key={tab.label} tab={tab} active={active} />;
        })}

        <div className="relative flex h-full items-end justify-center">
          <Link
            to="/kinh-dich"
            aria-current={castingActive ? "page" : undefined}
            className={cn(
              "group flex h-full w-full flex-col items-center justify-end gap-0.5 pb-1 transition",
              castingActive ? "text-primary" : "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "absolute -top-5 flex size-14 items-center justify-center rounded-full border-[5px] border-background bg-primary text-primary-foreground shadow-[0_5px_12px_rgba(154,105,32,0.22)] transition group-active:scale-95",
                castingActive && "ring-2 ring-primary/35",
              )}
            >
              <IconDongXu className="size-7" />
            </span>
            <span className="mt-auto text-[10px] font-semibold tracking-tight">Gieo quẻ</span>
          </Link>
        </div>

        <NavTab tab={tabs[2]} active={tabs[2].match(pathname)} />

        <button
          type="button"
          onClick={() => setAccountOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={accountOpen}
          className={cn(
            "flex h-full min-w-0 flex-col items-center justify-end gap-0.5 pb-1 text-[10px] font-medium tracking-tight transition",
            accountOpen || accountPaths.some((p) => pathname.startsWith(p))
              ? "text-primary"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <UserRound className="size-[21px]" />
          <span>Tôi</span>
        </button>
      </div>

      <HomeAccountSheet open={accountOpen} onOpenChange={setAccountOpen} />
    </nav>
  );
}

function NavTab({ tab, active }: { tab: (typeof tabs)[number]; active: boolean }) {
  return (
    <Link
      to={tab.to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex h-full min-w-0 flex-col items-center justify-end gap-0.5 pb-1 text-[10px] font-medium tracking-tight transition",
        active ? "text-primary" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <tab.icon className="size-[21px]" />
      <span>{tab.label}</span>
    </Link>
  );
}
